/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Grid,
  Segment
} from 'semantic-ui-react'
import concat from 'lodash/concat'
import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isBefore,
  startOfDay,
  subMinutes
} from 'date-fns'
// Local
import { formatDob } from '../../common/formatUtils'
import {
  API_URL,
  ENCOUNTER,
  FORMAT,
  LIST_MAX_PAGES,
  LIST_PAGE_SIZE,
  PATIENT,
  REVIEW,
  ROLES
} from '../../../constant'
import { api } from '../../../action/api'
import { searchGenerator } from '../../../action/search'
import SubHeader from '../../common/SubHeader'
import Table from '../../common/Table'
import { showVbcDisplay } from '../../common/currentUserUtils'
import { Encounter } from '../../authenticated/types'
// Styling
import './physicianWorklist.css'
import { RootState } from '../../../store'
import PhysicianRightSubHeader from './PhysicianRightSubHeader'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

/**
 * Provides a worklist view of encounters for the Physician user role
 * @param onSelect Callback function to execute when an encounter is selected arguments: Encounter ID
 * @param practitionerId: ID of current practitioner
 * @param viewDate Override default worklist view date with a specific date
 */

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  practitionerId: string
  viewDate: Date | string
  onSelect: (
    (
      encounterID: string,
      event: any
    ) => void
  ) | undefined
}

function PhysicianWorklist(
  props: Props
): JSX.Element {
  const {
    currentUser,
    practitionerId: practitionerIdProp,
    viewDate: viewDateProp,
    onSelect: onSelectProp
  } = props
  const [
    viewDateState,
    setViewDateState
  ] = useState<Date>(
    viewDateProp
      ? startOfDay(new Date(viewDateProp))
      : startOfDay(new Date())
  )
  const [
    practitionerIDState,
    setPractitionerIDState
  ] = useState<string>(practitionerIdProp)
  const [
    encountersState,
    setEncountersState
  ] = useState<Encounter[]>([])
  const [
    pagesDisplayedState,
    setPagesDisplayedState
  ] = useState<number>(0)
  const encounterSort = 'start asc'
  const fetchEncounters = (
    viewDate: Date,
    practitionerId: string,
    offset: number
  ): void => {
    const searchQuery = {
      start: viewDate,
      patientStatus: PATIENT.STATUS.ACTIVE,
      practitionerId,
      reviewCode: [
        REVIEW.CODE.READY_FOR_PHYSICIAN,
        REVIEW.CODE.READY_FOR_CODER
      ],
      encounterStatus: [
        ENCOUNTER.STATUS.PLANNED,
        ENCOUNTER.STATUS.ARRIVED,
        ENCOUNTER.STATUS.IN_PROGRESS,
        ENCOUNTER.STATUS.FINISHED
      ]
    }
    const url = API_URL.ENCOUNTER_SEARCH_WITH_ROLE(ROLES.PHYSICIAN)
    const limit = offset === 0
      ? LIST_PAGE_SIZE + 1
      : LIST_PAGE_SIZE
    const params = searchGenerator(
      searchQuery,
      limit,
      offset,
      encounterSort,
      undefined
    )
    api.get(url, { params }).then(({ data }) => {
      const results = offset === 0
        ? data
        : concat(
          encountersState,
          data
        )
      const pages = offset === 0
        ? 1
        : pagesDisplayedState + 1
      setViewDateState(viewDate)
      setEncountersState(results)
      setPractitionerIDState(practitionerId)
      setPagesDisplayedState(pages)
    })
  }

  useEffect(() => {
    const offset = 0
    if (practitionerIDState) {
      fetchEncounters(
        viewDateState,
        practitionerIDState,
        offset
      )
    }
  }, [])

  const dateTitle = (): string => {
    if (viewDateState.getTime() === startOfDay(new Date()).getTime()) {
      return 'Today\'s Patients'
    }
    if (viewDateState.getTime() === addDays(startOfDay(new Date()), 1).getTime()) {
      return 'Tomorrow\'s Patients'
    }
    if (viewDateState.getTime() === addDays(startOfDay(new Date()), -1).getTime()) {
      return 'Yesterday\'s Patients'
    }
    if (viewDateState.getTime() < new Date().getTime()) {
      return `Patients from ${format(viewDateState, FORMAT.DAY_DATE)}`
    }
    return `Patients on ${format(viewDateState, FORMAT.DAY_DATE)}`
  }

  const mobilePatientName = (patient: any) => (
    `${patient.firstName} ${patient.lastName} ( ${formatDob(patient.birthDate, FORMAT.DATE)} ) `
  )

  const rowClassName = (startDate: Date) => {
    const baseClass = 'cursor-pointer'
    const isOnSchedule = viewDateState.getTime() === startOfDay(new Date()).getTime()
    const encounterRange = 30
    if (!isOnSchedule) {
      return baseClass
    }
    const lowerLimit = subMinutes(new Date(), encounterRange)
    const upperLimit = addMinutes(new Date(), encounterRange)
    if (isBefore(startDate, lowerLimit)) {
      return `${baseClass} past`
    } if (isAfter(startDate, upperLimit)) {
      return `${baseClass} future`
    }
    return `${baseClass} current`
  }

  const generateTableData = (list: any) => {
    if (!list || list.length === 0) return []
    return list.map((record: any) => {
      const time = format(record.start, FORMAT.TIME)
      const name = `${record.patient.firstName} ${record.patient.lastName}`
      const nameDob = mobilePatientName(record.patient)
      const { vbcProgram } = record.patient
      const practitioner = `${record.practitioner.firstName} ${record.practitioner.lastName}`
      return {
        id: record.id,
        time,
        name,
        dob: formatDob(record.patient.birthDate, FORMAT.DATE),
        nameDob,
        vbcProgram,
        practitioner,
        meta: {
          className: rowClassName(record.start)
        }
      }
    })
  }

  const onMore = () => {
    const offset = LIST_PAGE_SIZE * pagesDisplayedState + 1
    fetchEncounters(
      viewDateState,
      practitionerIDState,
      offset
    )
  }

  const selectRecord = (
    id: string,
    event: any
  ) => {
    if (onSelectProp && id) {
      onSelectProp(id, event)
    }
  }

  const renderWorklistTable = () => {
    const tableData = generateTableData(encountersState)
    const computerColumns = [
      { name: 'Time', value: 'time' },
      { name: 'Name', value: 'name' },
      { name: 'DOB', value: 'dob' },
      { name: 'Practitioner', value: 'practitioner' }
    ]
    let shouldDisplayMore = false
    let shouldDisplayMaxRecords = false

    if (tableData.length > pagesDisplayedState * LIST_PAGE_SIZE) {
      shouldDisplayMore = pagesDisplayedState < LIST_MAX_PAGES
      shouldDisplayMaxRecords = pagesDisplayedState >= LIST_MAX_PAGES
      tableData.pop()
    }

    const table = (
      <Table
        computerColumns={ computerColumns }
        mobileColumns={ [
          { value: 'time', width: '4' },
          { value: 'nameDob', width: '12' }
        ] }
        data={ tableData }
        onClick={ selectRecord }
        more={ shouldDisplayMore }
        max={ shouldDisplayMaxRecords }
        onMore={ onMore }
        sortOnHeaderClick={ undefined }
      />
    )

    if (showVbcDisplay(currentUser)) {
      computerColumns.splice(
        3,
        0,
        {
          name: 'VBC Program',
          value: 'vbcProgram'
        }
      )
      return (
        <span>
          { table }
        </span>
      )
    }
    return (
      <span>
        { table }
      </span>
    )
  }

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left={ dateTitle() }
          right={
            <PhysicianRightSubHeader
              encountersState={ encountersState }
              fetchEncounters={ fetchEncounters }
              viewDateState={ viewDateState }
              practitionerIDState={ practitionerIDState }
            />
          }
        />
        <SafeSegment
          attached
          className="no-margin"
        >
          <SafeGrid>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                { renderWorklistTable() }
              </Grid.Column>
            </Grid.Row>
          </SafeGrid>
        </SafeSegment>
      </Container>
    </div>
  )
}

export default connect(mapStateToProps)(PhysicianWorklist)
